<template>
  <section class="person-hero">
    <div v-if="coverImage" class="person-hero__bg">
      <img :src="coverImage" alt="" />
    </div>
    <div v-else class="person-hero__bg person-hero__bg--placeholder" />
    <div class="person-hero__overlay" />

    <div class="person-hero__inner">
      <div class="person-hero__portrait">
        <EntityGallery :images="gallery" :alt="detail.title" :height="360" />
      </div>

      <div class="person-hero__info">
        <h1 class="person-hero__name">{{ detail.title }}</h1>
        <dl class="person-hero__meta">
          <div
            v-for="field in fields"
            :key="field.label"
            class="person-hero__meta-row"
          >
            <dt>{{ field.label }}：</dt>
            <dd>{{ field.value }}</dd>
          </div>
        </dl>
        <p class="person-hero__bio">{{ biography.value }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import { renameProvinceToNativePlace } from '@/utils/entityMeta'
import EntityGallery from './EntityGallery.vue'

export default {
  name: 'PersonHero',
  components: { EntityGallery },
  props: {
    detail: { type: Object, required: true }
  },
  computed: {
    gallery() {
      return this.detail.gallery || []
    },
    coverImage() {
      return this.gallery[0] || ''
    },
    fields() {
      const raw = (this.detail.fields || []).filter((field) => field.code !== 'biography')
      return renameProvinceToNativePlace(raw)
    },
    biography() {
      return this.detail.fields.find(field => field.code === 'biography') || { value: '' }
    }
  }
}
</script>

<style lang="scss" scoped>
.person-hero {
  position: relative;
  margin-top: $header-height;
  padding: $spacing-3xl $content-padding;
  overflow: hidden;

  &__bg {
    position: absolute;
    inset: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(0.6) sepia(0.3) brightness(0.4);
    }

    &--placeholder {
      background: linear-gradient(145deg, rgba($color-cream, 0.95), rgba($color-paper-light, 0.9));
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba($color-brown-dark, 0.88) 0%,
      rgba($color-brown-dark, 0.75) 100%
    );
  }

  &__inner {
    position: relative;
    z-index: 1;
    display: flex;
    gap: $spacing-2xl;
    max-width: $container-max-width;
    margin: 0 auto;
    align-items: flex-start;
  }

  &__portrait {
    position: relative;
    flex-shrink: 0;
    width: 280px;
    border-radius: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  &__info {
    flex: 1;
    min-width: 0;
    color: $color-white;
  }

  &__name {
    font-family: $font-serif;
    font-size: rem(36);
    font-weight: 700;
    letter-spacing: 4px;
    margin-bottom: $spacing-base;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &__meta {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: $spacing-sm $spacing-xl;
    margin-bottom: $spacing-base;
    padding-bottom: $spacing-base;
    border-bottom: 1px solid rgba($color-white, 0.2);
  }

  &__meta-row {
    display: flex;
    font-size: rem(14);
    line-height: 1.8;

    dt { opacity: 0.75; flex-shrink: 0; }
    dd { font-weight: 500; }
  }

  &__bio {
    font-size: rem(14);
    line-height: 1.9;
    opacity: 0.9;
  }
}

@media (max-width: 960px) {
  .person-hero {
    &__inner { flex-direction: column; align-items: center; }
    &__portrait { width: 100%; max-width: 280px; }
    &__meta { grid-template-columns: repeat(2, 1fr); }
  }
}
</style>
